import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/configure-store'
import { httpService } from '../../services/http-service'
import { SelectCompanyPrompt } from '../../cmps/general/select-company-prompt'
import { getEmployeesByCompanyNameQuery } from '../../cmps/util-cmps/graphql-queries/employees-query'
import { Spinner } from '../../cmps/util-cmps/spinner'
import { useCachedFetch } from '../../hooks/use-cached-fetch'
import GenericTable from '../../cmps/general/generic-table'
import { UserFormModal } from '../../cmps/user/user-form-modal'

interface RoleDetail {
    role: string
    audit_control_id: number
    system_name: string
    objective_name: string
    entity_name: string
}

interface DbUser {
    email: string
    first_name: string
    last_name: string
    roles: string[]
    role_details: RoleDetail[]
}

interface CognitoUser {
    username: string
    email: string
    status: string
    enabled: boolean
}

interface EmployeeRecord {
    email: string
    firstName: string
    lastName: string
    entity: { name: string }
}

interface UnifiedUser {
    email: string
    cognitoUsername: string
    firstName: string
    lastName: string
    source: 'New Flow' | 'Legacy' | 'Both'
    dbRoles: string[]
    roleDetails: RoleDetail[]
    entities: string[]
    cognitoStatus: string
    cognitoEnabled: string
}

const ROLE_RANK: Record<string, number> = {
    'overall-write': 0,
    'control-owner': 1,
    'ac-write': 2,
    'overall-read': 3,
    'ac-read': 4,
}

const ROLE_DISPLAY: Record<string, string> = {
    'overall-write': 'admin',
    'control-owner': 'control-owner',
    'ac-write': 'ac-write',
    'overall-read': 'admin-read',
    'ac-read': 'ac-read',
}

function formatRoles(roles: string[]): string {
    if (roles.length === 0) return '—'
    return roles.map(r => ROLE_DISPLAY[r] || r).join(', ')
}

function userHighestRank(user: UnifiedUser): number {
    if (user.dbRoles.length === 0) return 999
    return Math.min(...user.dbRoles.map(r => ROLE_RANK[r] ?? 99))
}

function mergeUsers(dbUsers: DbUser[], employees: EmployeeRecord[], cognitoUsers: CognitoUser[]): UnifiedUser[] {
    const map = new Map<string, UnifiedUser>()

    const getOrCreate = (email: string, firstName: string, lastName: string): UnifiedUser => {
        const key = email.toLowerCase()
        if (!map.has(key)) {
            map.set(key, {
                email,
                cognitoUsername: '',
                firstName,
                lastName,
                source: 'Legacy',
                dbRoles: [],
                roleDetails: [],
                entities: [],
                cognitoStatus: '—',
                cognitoEnabled: '—',
            })
        }
        return map.get(key)!
    }

    const sortRoles = (roles: string[]) =>
        [...roles].sort((a, b) => (ROLE_RANK[a] ?? 99) - (ROLE_RANK[b] ?? 99))

    for (const u of dbUsers) {
        const row = getOrCreate(u.email, u.first_name, u.last_name)
        row.source = 'New Flow'
        row.dbRoles = sortRoles(u.roles)
        row.roleDetails = u.role_details || []
        if (!row.firstName && u.first_name) row.firstName = u.first_name
        if (!row.lastName && u.last_name) row.lastName = u.last_name
    }

    for (const emp of employees) {
        const row = getOrCreate(emp.email, emp.firstName, emp.lastName)
        if (row.source === 'New Flow') {
            row.source = 'Both'
        }
        const entityName = emp.entity?.name
        if (entityName && !row.entities.includes(entityName)) {
            row.entities.push(entityName)
        }
        if (!row.firstName && emp.firstName) row.firstName = emp.firstName
        if (!row.lastName && emp.lastName) row.lastName = emp.lastName
    }

    for (const cu of cognitoUsers) {
        const key = cu.email.toLowerCase()
        if (map.has(key)) {
            const row = map.get(key)!
            row.cognitoUsername = cu.username || ''
            row.cognitoStatus = cu.status || '—'
            row.cognitoEnabled = cu.enabled ? 'Yes' : 'No'
        } else {
            map.set(key, {
                email: cu.email,
                cognitoUsername: cu.username || '',
                firstName: '',
                lastName: '',
                source: 'Legacy',
                dbRoles: [],
                roleDetails: [],
                entities: [],
                cognitoStatus: cu.status || '—',
                cognitoEnabled: cu.enabled ? 'Yes' : 'No',
            })
        }
    }

    return Array.from(map.values()).sort((a, b) => userHighestRank(a) - userHighestRank(b))
}

const MAPPING = [
    { title: 'DB Roles', location: 'dbRoles', type: 'string' },
    { title: 'Email', location: 'email', type: 'string' },
    { title: 'Cognito Username', location: 'cognitoUsername', type: 'string' },
    { title: 'First Name', location: 'firstName', type: 'string' },
    { title: 'Last Name', location: 'lastName', type: 'string' },
    { title: 'Source', location: 'source', type: 'string' },
    { title: 'Entities (Legacy)', location: 'entities', type: 'string' },
    { title: 'AWS Cognito', location: 'cognitoStatus', type: 'cognito' },
    { title: 'Actions', location: '', type: 'actions' },
]

function buildTableData(users: UnifiedUser[]) {
    return users.map(user => {
        return MAPPING.map(col => {
            if (col.type === 'actions') {
                return { title: col.title, value: '', type: col.type }
            }
            if (col.type === 'cognito') {
                // Pack both status and enabled into the value for the renderer
                const inCognito = user.cognitoStatus !== '—'
                return {
                    title: col.title,
                    value: inCognito ? user.cognitoStatus : '',
                    type: col.type,
                    cognitoEnabled: user.cognitoEnabled,
                }
            }
            let value: any = user[col.location as keyof UnifiedUser]
            if (col.location === 'dbRoles') {
                value = formatRoles(user.dbRoles)
            } else if (Array.isArray(value)) {
                value = value.length > 0 ? value.join(', ') : '—'
            }
            return { title: col.title, value: value ?? '—', type: col.type }
        })
    })
}

const renderExpandedRow = (rowData: any[], _rowIndex: number, users: UnifiedUser[]) => {
    const emailCell = rowData.find((c: any) => c.title === 'Email')
    const email = emailCell?.value || ''
    const user = users.find(u => u.email.toLowerCase() === email.toLowerCase())
    if (!user) return null

    const acRoleIds = new Set(user.roleDetails.map(rd => rd.role))
    const generalRoles = user.dbRoles.filter(r => !acRoleIds.has(r))
    const hasTableRows = user.roleDetails.length > 0 || user.entities.length > 0

    return (
        <div className="user-expanded-row">
            {generalRoles.length > 0 && (
                <div className="user-expanded-general-roles">
                    <span className="user-expanded-label">General Roles: </span>
                    <span className="role-badges">
                        {generalRoles.map((r, i) => {
                            const display = ROLE_DISPLAY[r] || r
                            let cls = 'role-badge-default'
                            if (display === 'admin') cls = 'role-badge-admin'
                            else if (display === 'admin-read') cls = 'role-badge-admin-read'
                            return <span key={i} className={`role-badge ${cls}`}>{display}</span>
                        })}
                    </span>
                </div>
            )}
            {hasTableRows && (
                <div>
                    <span className="user-expanded-title">
                        AC Assignments
                    </span>
                    <div className="user-role-table-scroll">
                        <table className="user-role-table">
                            <thead>
                                <tr>
                                    <th>Role</th>
                                    <th>AC ID</th>
                                    <th>Main System</th>
                                    <th>Objective</th>
                                    <th>Entity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {user.roleDetails.map((rd, i) => (
                                    <tr key={i}>
                                        <td>{rd.role}</td>
                                        <td>{rd.audit_control_id}</td>
                                        <td>{rd.system_name || '—'}</td>
                                        <td>{rd.objective_name}</td>
                                        <td>—</td>
                                    </tr>
                                ))}
                                {user.entities.map((ent, i) => (
                                    <tr key={`legacy-${i}`} className="legacy-row">
                                        <td>control-owner (legacy)</td>
                                        <td>—</td>
                                        <td>—</td>
                                        <td>—</td>
                                        <td>{ent}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {user.dbRoles.length === 0 && user.entities.length === 0 && (
                <p>No role details or entity assignments for this user.</p>
            )}
        </div>
    )
}

export const EmployeesPage: React.FC = () => {
    const companyName = useSelector((stateModule: RootState) => stateModule.companyName.value)

    const { data, isLoading, hasError, refetch } = useCachedFetch(
        companyName ? `unified-users:${companyName}` : null,
        async () => {
            const [dbUsersRes, employeesRes, cognitoRes] = await Promise.all([
                httpService.get('onboarding/admin-users', { company_name: companyName }).catch(() => ({ users: [] })),
                httpService.graphql(companyName, getEmployeesByCompanyNameQuery(companyName)).catch(() => ({ employeesByCompanyName: [] })),
                httpService.get('onboarding/cognito-group-users', { company_name: companyName }).catch(() => ({ users: [] })),
            ])

            const dbUsers: DbUser[] = dbUsersRes.users || []
            const employees: EmployeeRecord[] = employeesRes.employeesByCompanyName || []
            const cognitoUsers: CognitoUser[] = cognitoRes.users || []

            return mergeUsers(dbUsers, employees, cognitoUsers)
        }
    )

    const [isModalOpen, setIsModalOpen] = useState(false)
    const [userToEdit, setUserToEdit] = useState<UnifiedUser | null>(null)

    const users = data || []
    const tableData = buildTableData(users)

    const openCreateModal = () => {
        setUserToEdit(null)
        setIsModalOpen(true)
    }

    const openEditModal = (rowData: any[]) => {
        const emailCell = rowData.find((c: any) => c.title === 'Email')
        const email = emailCell?.value || ''
        const user = users.find(u => u.email.toLowerCase() === email.toLowerCase())
        if (!user) return
        // Only allow editing DB users (new flow or both)
        if (user.source === 'Legacy' && user.dbRoles.length === 0) {
            return // Legacy-only users can't be edited through this modal
        }
        setUserToEdit(user)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
        setUserToEdit(null)
    }

    const handleSuccess = () => {
        closeModal()
        refetch()
    }

    if (!companyName) {
        return <div className="tablePageContainer"><SelectCompanyPrompt /></div>
    }

    return <div className="tablePageContainer">
        <div className="page-header">
            <p className="tableSubtitle">Unified view of users across AudiTech database and AWS Cognito.</p>
            <button className="add-btn" onClick={openCreateModal}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Create User
            </button>
        </div>
        {isLoading && <Spinner />}
        {!isLoading && !hasError && (
            <GenericTable
                data={tableData}
                recordName="Users"
                filterableColumns={['Source']}
                renderExpandedRow={(rowData, rowIndex) => renderExpandedRow(rowData, rowIndex, users)}
                onEditClick={openEditModal}
                editColumn="Email"
            />
        )}
        {!isLoading && hasError && <div className="error-hint">Failed to load data. Try refreshing the page or re-logging in.</div>}

        {isModalOpen && (
            <UserFormModal
                key={userToEdit?.email || 'create'}
                companyName={companyName}
                userToEdit={userToEdit}
                existingEmails={users.map((u: any) => u.email)}
                onSuccess={handleSuccess}
                onClose={closeModal}
            />
        )}
    </div>
}
